import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import UserData from '../../../assets/data/UserData';
import ArrowRepeatIcon16pxSvg from '../../../assets/images/icons/ArrowRepeatIcon16pxSvg';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import { getMultipleUserRemoved, getUserList, restoreUser } from '../../../axios/User/UserApi';
import { ControlpanelSecondaryButton, ControlpanelSuccessButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, StyledDataGrid } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import CustomGridToolbarQuickFilter from '../CustomGridToolbarQuickFilter';
import CustomPagination from '../CustomPagination';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import ServerSearchInput from '../../../components/ServerSearchInput';
import { USER_STATUS } from '../../../assets/data/common';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAuth } from '../../../context/AuthContext';
import { useSettings } from '../../../context/SettingsContext';
dayjs.extend(utc);
dayjs.extend(timezone)


const users = UserData();

const CreateUserFromDeletedUsersDialog = ({ handleRefreshTable, onClose }) => {
   const { t } = useTranslation();
   const { genaralSettingDateFormat, genaralSettingTimeFormat, dateTimeFormat } =
      useAuth();
   const { settings } = useSettings();
   const [show, hide] = useLoading();
   const [gridLoading, setGridLoading] = useState(false);
   const [refresh, setRefresh] = useState(false);
   const [rowData, setRowData] = useState([]);
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(20);
   const [rowClick, setRowClick] = useState(null);
   const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
   const [rowCount, setRowCount] = useState(0);

   const convertFormat = (format) => {
      return format
         .replace(/dd/g, "DD")
         .replace(/MM/g, "MM")
         .replace(/yyyy/g, "YYYY");
   };

   const columnData = useMemo(
      () => [
         {
            field: "employeeCode",
            headerName: t("common.employee-code"),
            minWidth: 150,
            flex: 1,
            hideable: true,
            rowSpanValueGetter: (value, row) =>
               row ? `${row.id}-${row.employeeCode}` : value,
            valueFormatter: (value) => {
               return value === null || value === undefined || value === ""
                  ? "-"
                  : `${value}`;
            },
         },
         {
            field: "fullName",
            headerName: t("common.fullname"),
            minWidth: 200,
            editable: true,
         },
         {
            field: "telephone",
            headerName: t("common.phone"),
            minWidth: 150,
            flex: 1,
            hideable: true,
         },
         {
            field: "designation",
            headerName: t("common.designation"),
            minWidth: 150,
            flex: 1,
            hideable: true,
         },
         {
            field: "administratorRights",
            headerName: t("common.admin"),
            minWidth: 180,
            hideable: true,
            rowSpanValueGetter: (value, row) =>
               row ? `${row.id}-${row.administratorRights}` : value,
            renderCell: (params) => {
               const { administratorRights: rights, owner } = params.row || {};

               // mapping key dịch
               const roleLabelMap = {
                  EMPLOYEE: t("roles2.Employee"),
                  AUDITOR: t("roles2.Auditor"),
                  ADMIN: t("roles2.Admin"),
                  SUBCRIPTION: t("roles2.SubscriptionAdmin"),
               };

               const displayValue =
                  roleLabelMap[rights] ||
                  (rights !== null && rights !== undefined && rights !== ""
                     ? rights
                     : "-");

               return (
                  <p className="flex items-center">
                     <span className="mr-1">{displayValue}</span>
                     {owner && (
                        <span className="text-red-500">
                           ({t("common.content-125")})
                        </span>
                     )}
                  </p>
               );
            },
         },
         {
            field: "active",
            headerName: t("common.employee-status"),
            minWidth: 150,
            flex: 1,
            hideable: true,
            rowSpanValueGetter: (value, row) =>
               row ? `${row.id}-${row.active}` : value,
            valueGetter: (value) => {
               const status = value;
               if (status === USER_STATUS.unblock) {
                  return t("common.active");
               } else if (status === USER_STATUS.block) {
                  return t("common.blocked");
               } else if (status === USER_STATUS.pendingActivation) {
                  return t("common.pending-activation");
               } else {
                  return t("common.content-150");
               }
            },
         },
         {
            field: "userTypeId",
            headerName: t("common.user-type"),
            minWidth: 200,
            flex: 1,
            hideable: true,
            rowSpanValueGetter: (value, row) =>
               row && row.userTypeId != null
                  ? `${row.id}-${row.userTypeId}`
                  : value,
            valueFormatter: (value) => {
               if (value === null || value === undefined) return "-";
               if (value === 1) return t("common.full-time");
               if (value === 2) return t("common.part-time");
            },
         },
         {
            field: "createdTime",
            headerName: t("common.created-time"),
            minWidth: 200,
            flex: 1,
            hideable: true,
            valueFormatter: (value) => {
               if (!value) return "-";

               const format = convertFormat(dateTimeFormat);

               return dayjs
                  .utc(value, "YYYY-MM-DD HH:mm:ss")
                  .local()
                  .format(format);
            }
         },
         {
            field: "modifiedTime",
            headerName: t("common.modified-time"),
            minWidth: 200,
            flex: 1,
            hideable: true,
            valueFormatter: (value) => {
               if (!value) return "-";

               const format = convertFormat(dateTimeFormat);

               return dayjs
                  .utc(value, "YYYY-MM-DD HH:mm:ss")
                  .local()
                  .format(format);
            }
         },
      ],
      [t, settings, dateTimeFormat]
   );

   const [searchText, setSearchText] = useState('');

   const handleSearchChange = (value) => {
      setSearchText(value);
   };

   useEffect(() => {
      const handler = setTimeout(() => {
         setPage(0); // reset page khi search
         fetchData();
      }, 500); // debounce 500ms

      return () => clearTimeout(handler);
   }, [searchText, pageSize, page]); // chạy khi searchText thay đổi

   const fetchData = useCallback(async () => {
      try {
         show();
         const payload = {
            statusId: 0,
            page: page + 1,
            pageSize: pageSize,
            status: false,
            langId: localStorage.getItem('i18nextLngId'),
            searchText: searchText || null,
         };
         const response = await getUserList(payload);
         const employees = response.data?.data || [];
         setRowData(employees);
         setRowCount(response.data?.pagination?.totalElements || 0);
      } catch (error) {
         if (isAxiosError(error)) {
            if (error.response && error.response.status !== 401) {
               toast.error(t(error.response.data?.message || 'Error'));
            } else if (!error.response) {
               toast.error(t('common.network-timeout'));
            }
         } else {
            toast.error('Unexpected error');
         }
      } finally {
         hide();
      }
   }, [page, pageSize, searchText, show, hide, t]);
   // ---- debounce search + fetchData ----
   useEffect(() => {
   const handler = setTimeout(() => {
      setPage(0); // reset page khi search
      fetchData();
   }, 500); // debounce 500ms

   return () => clearTimeout(handler);
}, [searchText, pageSize, page, fetchData]);

   const templateToolbar = useCallback(
      () => (
         <div className="flex gap-2 justify-between mb-5">
            <div className="flex gap-3">

            </div>
            <ServerSearchInput onChange={handleSearchChange} />
         </div>
      ),
      [t]
   );

   const handleRowClick = (params) => {
      const rowData = params.row;
      if (rowData) {
         setRowClick(rowData);
      }
   };

   const handleRestore = async () => {
      try {
         if (!rowClick.userId) return;
         show();
         await restoreUser(rowClick.userId);
         await fetchData();
         toast.success(t("common.user-restored-successfully"));
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data));
            }
         } else {
            toast.error(t('common.network-timeout'));
         }
      } finally {
         hide();
         handleRefreshTable();
         onClose();
      }
   };

   const handleClose = () => {
      onClose();
      if (refresh) {
         handleRefreshTable();
      }
   };
   const handleRowsPerPageChange = (newPageSize) => {
      // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
      const size =
         typeof newPageSize === "number"
            ? newPageSize
            : parseInt(newPageSize.target.value, 10);

      setPage((prevPage) => {
         // index của record đầu tiên đang xem
         const firstRowIndex = prevPage * pageSize;

         // page mới sao cho vẫn chứa record đó
         const newPage = Math.floor(firstRowIndex / size);

         // phòng trường hợp vượt quá số trang
         const maxPage = Math.max(
            0,
            Math.ceil(rowCount / size) - 1
         );

         return Math.min(newPage, maxPage);
      });

      setPageSize(size);
   };

   return (
      <CustomDialog
         maxWidth={'xl'}
         open
         onClose={handleClose}
         keepMounted
         transitionDuration={window.__tourIsOpen ? 0 : undefined}
         data-tour="user-management-restore-dialog"
      >
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('button.create-user-from-deleted-users')}</p>
               </div>
               <GrayIconButton onClick={handleClose} className='self-start'>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='mt-5' data-tour="user-management-restore-table">
               <StyledDataGrid
                  sx={{
                     height: 400,

                     ".MuiDataGrid-main": {
                        height: "70vh",
                     },

                     "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                     {
                        display: "none !important",
                     },

                     "& .MuiDataGrid-scrollbarFiller": {
                        display: "none !important",
                     },
                     "& .MuiDataGrid-columnHeaders": {
                        paddingRight: 0,
                     },
                     "& .MuiDataGrid-topContainer": {
                        paddingRight: 0,
                     },

                     "&": {
                        "--DataGrid-scrollbarSize": "0px",
                     },

                     "& .MuiDataGrid-virtualScroller": {
                        scrollbarWidth: "none",
                     },
                     "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                        width: 0,
                        height: 0,
                     },
                     "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                     {
                        scrollbarWidth: "thin",
                     },
                     "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                     {
                        width: 10,
                        height: 10,
                     },

                     '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                        display: "none !important",
                     },

                     "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                     },

                     "& input[type=number]": {
                        textAlign: "center",
                        MozAppearance: "textfield",
                        WebkitAppearance: "none",
                        padding: "5px !important",
                        height: "100%",
                        pointerEvents: "none",
                        caretColor: "transparent",
                        userSelect: "none",
                        paddingTop: "8px !important",
                        paddingBottom: "8px !important"
                     },
                  }}
                  rows={rowData}
                  columns={columnData}
                  getRowId={(v) => v.userId}
                  slots={{
                     toolbar: templateToolbar,
                     pagination: CustomPagination,
                     noRowsOverlay: NoRowOverlay,
                     columnsPanel: CustomColumnsPanel,
                  }}
                  slotProps={{
                     pagination: {
                        page,
                        count: rowCount,
                        rowsPerPage: pageSize,
                        onPageChange: setPage,
                        onRowsPerPageChange: handleRowsPerPageChange,
                     },
                  }}
                  localeText={tableTranslated}
                  onRowClick={handleRowClick}
               />
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleClose}>
                  <CloseIcon />
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelSuccessButton
                  data-tour="user-management-restore-send-button"
                  onClick={handleRestore}
                  style={{ pointerEvents: !rowClick ? 'none' : 'auto', opacity: !rowClick ? 0.5 : 1 }}
               >
                  <ArrowRepeatIcon16pxSvg />
                  <p>{t('button.restore')}</p>
               </ControlpanelSuccessButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default CreateUserFromDeletedUsersDialog;
